import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function Cadastro() {
  // Força o fundo do body com imagem e gradiente apenas nesta página
  useEffect(() => {
    const originalBg = document.body.style.background;
    document.body.style.background = `
      linear-gradient(135deg, rgba(25, 118, 210, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%),
      url('${process.env.PUBLIC_URL}/assets/fundo_login.jpg') center/cover no-repeat fixed
    `;
    return () => {
      document.body.style.background = originalBg;
    };
  }, []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [token, setToken] = useState('');
  // Para inputs separados do código
  const [tokenDigits, setTokenDigits] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [tokenError, setTokenError] = useState('');
  
  const [formData, setFormData] = useState({
    nomeClinica: '',
    tipoPessoa: '',
    cnpj: '',
    nome: '',
    email: '',
    telefoneClinica: '',
    senha: '',
    confirmarSenha: ''
  });

  // Máscara de telefone brasileiro (celular ou fixo)
  function formatarTelefone(value) {
    if (!value) return '';
    let v = value.replace(/\D/g, '');
    if (v.length > 11) v = v.slice(0, 11);
    if (v.length > 10) {
      // Celular: (99) 99999-9999
      return v.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (v.length > 6) {
      // Fixo: (99) 9999-9999
      return v.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else if (v.length > 2) {
      return v.replace(/(\d{2})(\d{0,5})/, '($1) $2');
    } else {
      return v;
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telefoneClinica') {
      setFormData(prev => ({
        ...prev,
        telefoneClinica: formatarTelefone(value)
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpa o CNPJ se mudar para PF
    if (name === 'tipoPessoa' && value === 'PF') {
      setFormData(prev => ({
        ...prev,
        cnpj: ''
      }));
    }
  };

  const validarCNPJ = (cnpj) => {
    // Remove caracteres não numéricos
    const cnpjLimpo = cnpj.replace(/[^\d]/g, '');
    return cnpjLimpo.length === 14;
  };

  const formatarCNPJ = (value) => {
    const cnpj = value.replace(/[^\d]/g, '');
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  };

  const handleCNPJChange = (e) => {
    const formatted = formatarCNPJ(e.target.value);
    setFormData(prev => ({
      ...prev,
      cnpj: formatted
    }));
  };

  const validarFormulario = () => {
    if (!formData.nomeClinica) {
      setError('Por favor, informe o nome da clínica');
      return false;
    }

    if (!formData.tipoPessoa) {
      setError('Por favor, selecione o tipo de pessoa');
      return false;
    }

    if ((formData.tipoPessoa === 'PJ' || formData.tipoPessoa === 'HIBRIDO') && !formData.cnpj) {
      setError('Por favor, informe o CNPJ');
      return false;
    }

    if ((formData.tipoPessoa === 'PJ' || formData.tipoPessoa === 'HIBRIDO') && !validarCNPJ(formData.cnpj)) {
      setError('CNPJ inválido');
      return false;
    }

    if (!formData.nome) {
      setError('Por favor, informe o nome do dentista');
      return false;
    }

    if (!formData.email) {
      setError('Por favor, informe o email');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Email inválido');
      return false;
    }

    if (!formData.senha) {
      setError('Por favor, informe a senha');
      return false;
    }

    if (formData.senha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }

    if (formData.senha !== formData.confirmarSenha) {
      setError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  // Função de validação do token (deve estar no escopo do componente)
  const handleValidateToken = async () => {
    setTokenError('');
    const code = tokenDigits.join('');
    if (!code || code.length !== 6) {
      setTokenError('Digite o código de 6 dígitos.');
      return;
    }
    try {
      await api.post('/auth/validate-email-token', {
        email: formData.email,
        token: code,
      });
      // Limpa erro e fecha modal
      setTokenError('');
      setShowTokenModal(false);
      // Tenta login automático após validação
      try {
        const loginResponse = await api.post('/auth/login', {
          email: formData.email,
          senha: formData.senha
        });
        localStorage.setItem('token', loginResponse.data.token);
        localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
        setSuccess('E-mail validado com sucesso! Redirecionando...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (loginErr) {
        setSuccess('E-mail validado com sucesso! Redirecionando para login...');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      setTokenError(
        err.response?.data?.message || 'Código inválido ou expirado.'
      );
    }
  };

  // Lida com digitação nos quadradinhos
  const handleTokenInput = (e, idx) => {
    const val = e.target.value.replace(/[^\d]/g, '');
    if (!val) {
      // Se apagou
      const newDigits = [...tokenDigits];
      newDigits[idx] = '';
      setTokenDigits(newDigits);
      setToken(newDigits.join(''));
      if (idx > 0) inputRefs.current[idx - 1]?.focus();
      return;
    }
    if (val.length === 1) {
      const newDigits = [...tokenDigits];
      newDigits[idx] = val;
      setTokenDigits(newDigits);
      setToken(newDigits.join(''));
      if (idx < 5) inputRefs.current[idx + 1]?.focus();
    } else if (val.length > 1) {
      // Colou vários dígitos
      const arr = val.split('').slice(0, 6 - idx);
      const newDigits = [...tokenDigits];
      arr.forEach((digit, i) => {
        newDigits[idx + i] = digit;
      });
      setTokenDigits(newDigits);
      setToken(newDigits.join(''));
      const nextIdx = Math.min(idx + arr.length, 5);
      inputRefs.current[nextIdx]?.focus();
    }
  };

  // Limpa os quadradinhos ao abrir o modal
  useEffect(() => {
    if (showTokenModal) {
      setTokenDigits(['', '', '', '', '', '']);
      setToken('');
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 200);
    }
  }, [showTokenModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        nomeClinica: formData.nomeClinica,
        tipoPessoa: formData.tipoPessoa,
        cnpj: formData.cnpj.replace(/[^\d]/g, ''), // Remove formatação
        nome: formData.nome,
        email: formData.email,
        telefoneClinica: formData.telefoneClinica.replace(/[^\d]/g, ''),
        senha: formData.senha
      });

      setSuccess('Cadastro realizado com sucesso! Verifique seu e-mail para validar.');
      // Salva o token (se necessário)
      localStorage.setItem('token', response.token);
      // Exibe o modal de validação de token
      setShowTokenModal(true);

    } catch (err) {
      console.error('Erro ao cadastrar:', err);
      setError(
        err.response?.data?.message || 
        'Erro ao realizar cadastro. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Paper
          elevation={16}
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            backdropFilter: 'blur(8px)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <img
              src={process.env.PUBLIC_URL + '/assets/gerencie_logo_nova.png'}
              alt="Logo Gerencie"
              style={{ width: 80, marginBottom: 8 }}
            />
            <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
              Criar Conta
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Gestão de clínicas de forma simples
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              label="Nome da Clínica"
              name="nomeClinica"
              value={formData.nomeClinica}
              onChange={handleChange}
              margin="dense"
              variant="outlined"
              required
              placeholder="Ex: Clínica Odonto Plus"
              size="small"
              InputProps={{ style: { color: '#222' } }}
            />

            <FormControl fullWidth margin="dense" required size="small">
              <InputLabel>Tipo de Pessoa</InputLabel>
              <Select
                name="tipoPessoa"
                value={formData.tipoPessoa}
                onChange={handleChange}
                label="Tipo de Pessoa"
              >
                <MenuItem value="PF">Pessoa Física</MenuItem>
                <MenuItem value="PJ">Pessoa Jurídica (CNPJ)</MenuItem>
                <MenuItem value="HIBRIDO">Híbrido (PF + PJ)</MenuItem>
              </Select>
            </FormControl>

            {(formData.tipoPessoa === 'PJ' || formData.tipoPessoa === 'HIBRIDO') && (
              <TextField
                fullWidth
                label="CNPJ"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleCNPJChange}
                margin="dense"
                variant="outlined"
                required
                placeholder="00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
                size="small"
                InputProps={{ style: { color: '#222' } }}
              />
            )}

            <TextField
              fullWidth
              label="Nome do Dentista"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              margin="dense"
              variant="outlined"
              required
              placeholder="Seu nome completo"
              size="small"
              InputProps={{ style: { color: '#222' } }}
            />


            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', mb: 0.5 }}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="dense"
                variant="outlined"
                required
                placeholder="seu@email.com"
                size="small"
                InputProps={{ style: { color: '#222' } }}
                sx={{ flex: 2 }}
              />
              <TextField
                label="Telefone da Clínica"
                name="telefoneClinica"
                value={formData.telefoneClinica || ''}
                onChange={handleChange}
                margin="dense"
                variant="outlined"
                required
                placeholder="(99) 99999-9999"
                size="small"
                InputProps={{ style: { color: '#222' } }}
                sx={{ flex: 1, minWidth: 150 }}
                inputProps={{ maxLength: 15 }}
              />
            </Box>

            {/* Modal de validação de token */}
            <Dialog open={showTokenModal} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
              <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 22, pb: 0 }}>
                <span role="img" aria-label="email">📧</span> Validação de E-mail
              </DialogTitle>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
                <Typography sx={{ mb: 2, textAlign: 'center', color: 'text.secondary', fontSize: 15 }}>
                  Digite o código de 6 dígitos enviado para seu e-mail:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {tokenDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={el => inputRefs.current[idx] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleTokenInput(e, idx)}
                      onPaste={e => handleTokenInput(e, idx)}
                      onFocus={e => e.target.select()}
                      style={{
                        width: 38,
                        height: 44,
                        fontSize: 26,
                        textAlign: 'center',
                        border: '2px solid #1976d2',
                        borderRadius: 8,
                        outline: 'none',
                        background: '#f7fafd',
                        marginRight: idx < 5 ? 4 : 0,
                        fontWeight: 600,
                        letterSpacing: 1,
                        transition: 'border 0.2s',
                      }}
                    />
                  ))}
                </Box>
                {tokenError && (
                  <Alert severity="error" sx={{ mt: 1, width: '100%' }}>{tokenError}</Alert>
                )}
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button onClick={() => setShowTokenModal(false)} color="secondary" variant="outlined">Cancelar</Button>
                <Button onClick={handleValidateToken} variant="contained" color="primary">Validar</Button>
              </DialogActions>
            </Dialog>

            <TextField
              fullWidth
              label="Senha"
              name="senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.senha}
              onChange={handleChange}
              margin="dense"
              variant="outlined"
              required
              placeholder="Mínimo 6 caracteres"
              size="small"
              InputProps={{
                style: { color: '#222' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Confirmar Senha"
              name="confirmarSenha"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmarSenha}
              onChange={handleChange}
              margin="dense"
              variant="outlined"
              required
              placeholder="Digite a senha novamente"
              size="small"
              InputProps={{ style: { color: '#222' } }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 1.5,
                py: 1.3,
                fontWeight: 600,
                fontSize: '1.1rem',
                background: 'linear-gradient(45deg, #1976d2 30%, #ff9800 90%)',
                boxShadow: '0 3px 8px 2px rgba(25, 118, 210, .10)',
                borderRadius: 2,
                letterSpacing: 1,
                '&:hover': {
                  background: 'linear-gradient(45deg, #ff9800 30%, #1976d2 90%)',
                }
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Criar Conta'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Já tem uma conta?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer', fontWeight: 500 }}
                >
                  Fazer Login
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default Cadastro;
